import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import dayjs from "dayjs";
import { CalendarClock, Trees, Users, Sparkles, HandCoins, ClipboardList, Feather, Compass } from "lucide-react";

// 2D · Living Calendar (Lean-Core Pass 2)
// Unified time-anchored view over existing collections. Categories per mandate §3D.
const CATEGORY_META = {
  HOSPITALITY:   { label: "Hospitality",   icon: Trees,         accent: "forest" },
  FUNDING:       { label: "Funding",       icon: HandCoins,     accent: "terracotta" },
  DEADLINE:      { label: "Deadline",      icon: CalendarClock, accent: "terracotta" },
  PEOPLE:        { label: "People",        icon: Users,         accent: "forest" },
  INSTITUTIONAL: { label: "Institutional", icon: Feather,       accent: "forest" },
  FIELD:         { label: "Field",         icon: Compass,       accent: "forest" },
  CULTIVATION:   { label: "Cultivation",   icon: Sparkles,      accent: "forest" },
  PROGRAM:       { label: "Program",       icon: ClipboardList, accent: "forest" },
  PUBLICATION:   { label: "Publication",   icon: Feather,       accent: "terracotta" },
};

export default function LivingCalendar() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [days, setDays] = useState(60);
  useEffect(() => {
    api.get(`/living-calendar?days_ahead=${days}`).then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || e.message));
  }, [days]);

  if (err) return <div className="p-8 text-red-700" data-testid="calendar-error">Living Calendar failed: {err}</div>;
  if (!data) return <div className="p-8 text-forest/60" data-testid="calendar-loading">Loading Living Calendar…</div>;

  const active = new Set(Object.keys(data.counts_by_category || {}));
  const notConnected = data.categories_pending_activation || [];

  return (
    <div className="space-y-6" data-testid="living-calendar-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Command Surface</div>
          <h1 className="font-serif text-4xl mt-1">Living Calendar</h1>
          <p className="text-ink-soft text-sm">Window: {data.window.start} → {data.window.end} · {data.count} items</p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="text-ink-soft">Days ahead:</span>
          {[30, 60, 90, 180].map(d => (
            <button key={d} onClick={() => setDays(d)} data-testid={`calendar-days-${d}`}
                    className={`px-2 py-1 rounded border ${d === days ? "bg-forest text-bone border-forest" : "border-line hover:bg-bone"}`}>
              {d}
            </button>
          ))}
        </div>
      </header>

      <section className="bg-admin-surface border border-line rounded-xl p-4" data-testid="calendar-category-summary">
        <div className="overline mb-3">Categories present in window</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(CATEGORY_META).map(([key, meta]) => {
            const count = data.counts_by_category?.[key] || 0;
            const connected = active.has(key) || !notConnected.includes(key);
            const Icon = meta.icon;
            return (
              <span key={key} className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs
                  ${connected ? (count > 0 ? (meta.accent === "terracotta" ? "border-terracotta/40 bg-terracotta/10 text-terracotta" : "border-forest/40 bg-forest/10 text-forest") : "border-line bg-bone text-ink-soft") : "border-dashed border-line bg-bone text-ink-soft"}`}
                    data-testid={`calendar-category-${key.toLowerCase()}`}>
                <Icon className="h-3 w-3" strokeWidth={1.5} />
                {meta.label} · {connected ? count : "NOT_YET_CONNECTED"}
              </span>
            );
          })}
        </div>
        {notConnected.length > 0 && (
          <div className="text-[11px] text-ink-soft italic mt-3">
            {notConnected.join(" · ")} rails have no source collection yet. Honest NOT_YET_CONNECTED — mandate §3D.
          </div>
        )}
      </section>

      <section className="bg-admin-surface border border-line rounded-xl overflow-hidden" data-testid="calendar-events">
        <div className="px-5 py-3 border-b border-line bg-bone/40 overline">Events</div>
        {data.events.length === 0
          ? <div className="p-6 text-sm text-ink-soft italic">No time-anchored items in the window.</div>
          : <ul className="divide-y divide-line">
              {data.events.map(e => {
                const meta = CATEGORY_META[e.category] || CATEGORY_META.INSTITUTIONAL;
                const Icon = meta.icon;
                return (
                  <li key={e.id} className="px-5 py-3 flex items-start gap-3 hover:bg-bone/40" data-testid={`calendar-event-${e.id.split(':')[0]}`}>
                    <div className={`w-14 text-xs text-center ${meta.accent === "terracotta" ? "text-terracotta" : "text-forest"}`}>
                      <Icon className="h-4 w-4 mx-auto mb-1" strokeWidth={1.5} />
                      <div className="uppercase tracking-widest text-[9px]">{e.category}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{e.title}</div>
                      {e.detail && <div className="text-xs text-ink-soft truncate">{e.detail}</div>}
                    </div>
                    <div className="text-right text-xs">
                      <div className="text-forest/80">{e.date ? dayjs(e.date).format("MMM D") : "no date"}</div>
                      {e.status && <div className="text-ink-soft text-[10px] uppercase">{e.status}</div>}
                    </div>
                  </li>
                );
              })}
            </ul>}
      </section>

      <div className="text-xs text-ink-soft italic" data-testid="calendar-note">{data.note}</div>
    </div>
  );
}
